import {
  CustomGroupSectionNode,
  CustomSectionNode,
} from 'api/custom-section/types';
import {
  JSONContent,
  OperationPayload,
  TiptapJson,
} from 'api/page-template/types';
import { isEqual } from 'lodash';
import { isCustomGroupSection } from 'components/organisms/document-editor/extensions/custom-group-section/utils';
import { isCustomSectionNode } from 'components/organisms/document-editor/extensions/custom-section/utils/node-utils';

const commonKeys = [
  'sectionType',
  'contentState',
  'templateSectionId',
  'documentSectionId',
  'title',
  'config',
];

/** Keys that define RL_GROUP_SECTION identity for comparison (ignore transient editor attrs). */
const groupSectionCompareKeys = [
  'templateSectionId',
  'title',
  'config',
  'sectionType',
  'contentState',
  'state',
];

/** Treat null and undefined as equal so editor-added nulls don't cause false PUTs. */
const normAttr = (v: unknown): unknown =>
  v === null || v === undefined ? undefined : v;

const isSameCustomNode = (
  source: CustomSectionNode | CustomGroupSectionNode,
  other: CustomSectionNode | CustomGroupSectionNode,
) => {
  if (source.type !== other.type) {
    return false;
  }

  if (isCustomGroupSection(source)) {
    const sameAttrs = groupSectionCompareKeys.every((key) =>
      isEqual(normAttr(source.attrs?.[key]), normAttr(other.attrs?.[key])),
    );

    return (
      sameAttrs &&
      (source.content?.length ?? 0) === (other.content?.length ?? 0)
    );
  }

  return commonKeys.every((key) =>
    isEqual(normAttr(source.attrs?.[key]), normAttr(other.attrs?.[key])),
  );
};

export const getCustomSections = (
  nodes: JSONContent[] = [],
): (CustomSectionNode | CustomGroupSectionNode)[] => {
  let result: (CustomSectionNode | CustomGroupSectionNode)[] = [];

  for (const node of nodes) {
    if (isCustomSectionNode(node) || isCustomGroupSection(node)) {
      result.push(node as CustomSectionNode | CustomGroupSectionNode);
    }

    if (isCustomGroupSection(node) && Array.isArray(node.content)) {
      result = result.concat(getCustomSections(node.content));
    }
  }

  return result;
};

export const getDifferences = (
  tiptapJson: TiptapJson,
  previousJson: TiptapJson,
) => {
  const previousCustomSections = getCustomSections(previousJson?.content);
  const latestCustomSections = getCustomSections(tiptapJson?.content);

  if (previousCustomSections.length === 0) {
    return {
      sectionsAdded: latestCustomSections,
      sectionsRemoved: [],
      sectionsUpdated: [],
    };
  }

  const previousSectionsMap = new Map(
    previousCustomSections.map((section) => [
      section.attrs?.templateSectionId,
      section,
    ]),
  );
  const latestSectionsMap = new Map(
    latestCustomSections.map((section) => [
      section.attrs?.templateSectionId,
      section,
    ]),
  );
  const sectionsAdded = latestCustomSections.filter(
    (section) => !previousSectionsMap.has(section.attrs?.templateSectionId),
  );
  const sectionsRemoved = previousCustomSections.filter(
    (section) => !latestSectionsMap.has(section.attrs?.templateSectionId),
  );
  const sectionsUpdated = latestCustomSections.filter((section) => {
    const templateSectionId = section.attrs?.templateSectionId;

    if (!templateSectionId || !previousSectionsMap.has(templateSectionId)) {
      return false;
    }

    const previousSection = previousSectionsMap.get(templateSectionId);

    if (previousSection == null) {
      return false;
    }

    return !isSameCustomNode(section, previousSection);
  });

  return {
    sectionsAdded,
    sectionsRemoved,
    sectionsUpdated,
  };
};

export const getOperations = (
  tiptapJson: TiptapJson,
  previousJson: TiptapJson,
): OperationPayload[] => {
  const { sectionsAdded, sectionsRemoved, sectionsUpdated } = getDifferences(
    tiptapJson,
    previousJson,
  );

  return [
    ...sectionsAdded.map((section) => ({
      operation: 'POST' as const,
      payload: section,
    })),
    ...sectionsRemoved.map((section) => ({
      operation: 'DELETE' as const,
      payload: section,
    })),
    ...sectionsUpdated.map((section) => ({
      operation: 'PUT' as const,
      payload: section,
    })),
  ];
};
